import { useEffect, useState } from "react";
import api, { API } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { Logo, Loading, StatCard, inr, EmptyState } from "../../components/shared";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../components/ui/tabs";
import { Avatar, AvatarFallback, AvatarImage } from "../../components/ui/avatar";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from "../../components/ui/dropdown-menu";
import { toast } from "sonner";
import { Inbox, FileText, Briefcase, LogOut, ChevronDown, FileSignature, ShieldCheck, Wrench, Send, Plus, Trash2, Download } from "lucide-react";
import { ApplicationDocuments } from "../../components/ApplicationDocuments";
import VendorLayout from "../../components/VendorLayout";

export default function VendorPortal() {
  return <VendorLayout />;
}

function VendorVerification({ vendor }) {
  const [docs, setDocs] = useState([]);
  const [docType, setDocType] = useState("GST Certificate");
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/vendor/documents").then((r) => setDocs(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const upload = async (e) => {
    const file = e.target.files[0]; if (!file) return;
    setBusy(true);
    const fd = new FormData(); fd.append("file", file); fd.append("doc_type", docType);
    try { await api.post("/vendor/documents", fd); toast.success("Document uploaded for review"); load(); }
    catch (err) { toast.error(err.response?.data?.detail || "Upload failed"); } finally { setBusy(false); e.target.value = ""; }
  };
  return (
    <div className="max-w-2xl">
      <div className={`rounded-xl p-4 mb-4 text-sm ${vendor.verification_status === "APPROVED" || vendor.status === "Verified" ? "bg-secondary/10 border border-secondary/30 text-secondary" : "bg-amber-50 border border-amber-200 text-amber-800"}`}>
        Status: <b>{vendor.status}</b>. {vendor.verification_status === "APPROVED" || vendor.status === "Verified" ? "Your documents are under review / verified." : "Upload GST, PAN and certificates. Our team reviews your application and approves your account."}
      </div>
      <div className="bg-card border rounded-xl p-5">
        <Label className="text-sm mb-1 block">Document Type</Label>
        <select className="border rounded-md h-10 px-3 text-sm w-full mb-3" value={docType} onChange={(e) => setDocType(e.target.value)} data-testid="v-doctype">
          {["GST Certificate", "PAN Card", "Udyam / MSME", "Work Certificate", "Insurance", "Other"].map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
        <label className="inline-flex items-center gap-2 cursor-pointer bg-primary text-white rounded-full px-4 py-2 text-sm font-medium hover:opacity-90" data-testid="v-doc-upload-label">
          <input type="file" hidden onChange={upload} data-testid="v-doc-upload" />
          <Download className="w-4 h-4 rotate-180" /> {busy ? "Uploading..." : "Upload Document"}
        </label>
        <div className="mt-4 space-y-2">
          {docs.length === 0 ? <p className="text-sm text-muted-foreground">No documents uploaded yet.</p> :
            docs.map((d) => (
              <div key={d.vdoc_id} className="flex items-center justify-between border rounded-lg p-2.5 text-sm" data-testid={`v-doc-${d.vdoc_id}`}>
                <div><span className="font-medium">{d.doc_type}</span> <span className="text-muted-foreground text-xs">· {d.filename}</span></div>
                <span className="text-xs text-muted-foreground">{(d.uploaded_at || "").slice(0, 10)}</span>
              </div>
            ))}
        </div>
      </div>
    </div>
  );
}

function RfqCard({ rfq, onDone }) {
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!form.price) { toast.error("Enter your price"); return; }
    setBusy(true);
    try { await api.post(`/rfqs/${rfq.rfq_id}/quote`, { ...form, price: +form.price }); toast.success("Quote submitted"); onDone(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); } finally { setBusy(false); }
  };
  return (
    <div className="bg-card border rounded-xl p-5" data-testid={`v-rfq-${rfq.rfq_id}`}>
      <div className="flex items-center justify-between">
        <div><div className="font-mono text-xs text-muted-foreground">{rfq.ref}</div><h3 className="font-display font-bold">{rfq.title}</h3>
          <div className="text-xs text-muted-foreground">{rfq.category} · {rfq.society_name} · Deadline: {rfq.deadline || "-"}</div></div>
      </div>
      {rfq.boq?.items?.length > 0 && (
        <div className="mt-3 border rounded-lg overflow-hidden text-sm">
          <div className="bg-muted px-3 py-1.5 font-medium text-xs">BOQ</div>
          {rfq.boq.items.map((it, i) => <div key={i} className="flex justify-between px-3 py-1.5 border-t"><span>{it.description}</span><span className="text-muted-foreground">{it.qty} {it.unit} × {inr(it.rate)}</span></div>)}
        </div>
      )}
      {rfq.already_quoted ? <div className="mt-3 text-sm text-secondary">✓ You have already submitted a quote for this RFQ.</div> : (
        <div className="mt-3 grid sm:grid-cols-2 gap-2">
          <Input type="number" placeholder="Your price (₹)" value={form.price || ""} onChange={(e) => setForm({ ...form, price: e.target.value })} data-testid={`v-quote-price-${rfq.rfq_id}`} />
          <Input placeholder="Warranty" value={form.warranty || ""} onChange={(e) => setForm({ ...form, warranty: e.target.value })} />
          <Input placeholder="Timeline" value={form.timeline || ""} onChange={(e) => setForm({ ...form, timeline: e.target.value })} />
          <Input placeholder="Payment terms" value={form.payment_terms || ""} onChange={(e) => setForm({ ...form, payment_terms: e.target.value })} />
          <Textarea placeholder="Scope / material / exclusions" className="sm:col-span-2" value={form.scope || ""} onChange={(e) => setForm({ ...form, scope: e.target.value })} />
          <Button onClick={submit} disabled={busy} className="rounded-full gap-2 sm:col-span-2" data-testid={`v-submit-quote-${rfq.rfq_id}`}><Send className="w-4 h-4" /> Submit Quote</Button>
        </div>
      )}
    </div>
  );
}

const TOOLS = [
  { kind: "quotation", label: "Quotation", icon: FileText, fields: ["client", "validity", "warranty", "terms"], items: true },
  { kind: "invoice", label: "Invoice", icon: FileSignature, fields: ["client"], items: true },
  { kind: "service-report", label: "Service Report", icon: Wrench, fields: ["client", "site", "work_done", "observations"], items: false },
  { kind: "warranty", label: "Warranty Certificate", icon: ShieldCheck, fields: ["client", "work", "period", "valid_till", "terms"], items: false },
];

function VendorTools() {
  const [active, setActive] = useState(TOOLS[0]);
  const [form, setForm] = useState({});
  const [items, setItems] = useState([{ description: "", qty: "", rate: "" }]);
  const [busy, setBusy] = useState(false);

  const generate = async () => {
    setBusy(true);
    try {
      const body = { ...form };
      if (active.items) body.items = items.filter((i) => i.description);
      const res = await api.post(`/pdf/vendor/${active.kind}`, body, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      window.open(url, "_blank");
      toast.success(`${active.label} generated`);
    } catch (e) { toast.error("Could not generate PDF"); } finally { setBusy(false); }
  };

  return (
    <div className="grid lg:grid-cols-[220px_1fr] gap-6">
      <div className="flex lg:flex-col gap-2">
        {TOOLS.map((t) => (
          <button key={t.kind} onClick={() => { setActive(t); setForm({}); }} data-testid={`v-tool-${t.kind}`}
            className={`flex items-center gap-2 px-3 py-2.5 rounded-lg text-sm font-medium text-left ${active.kind === t.kind ? "bg-primary text-white" : "bg-card border hover:bg-muted"}`}>
            <t.icon className="w-4 h-4" /> {t.label}
          </button>
        ))}
      </div>
      <div className="bg-card border rounded-xl p-5 space-y-3">
        <h3 className="font-display font-bold">{active.label} Generator</h3>
        {active.fields.map((f) => (
          <div key={f}>
            <Label className="text-sm mb-1 block capitalize">{f.replace(/_/g, " ")}</Label>
            {["work_done", "observations", "terms"].includes(f)
              ? <Textarea value={form[f] || ""} onChange={(e) => setForm({ ...form, [f]: e.target.value })} data-testid={`v-field-${f}`} />
              : <Input value={form[f] || ""} onChange={(e) => setForm({ ...form, [f]: e.target.value })} data-testid={`v-field-${f}`} />}
          </div>
        ))}
        {active.items && (
          <div>
            <Label className="text-sm mb-1 block">Line Items</Label>
            {items.map((it, i) => (
              <div key={i} className="flex gap-2 mb-2">
                <Input className="flex-1" placeholder="Description" value={it.description} onChange={(e) => setItems(items.map((x, idx) => idx === i ? { ...x, description: e.target.value } : x))} data-testid={`v-item-desc-${i}`} />
                <Input className="w-20" type="number" placeholder="Qty" value={it.qty} onChange={(e) => setItems(items.map((x, idx) => idx === i ? { ...x, qty: e.target.value } : x))} />
                <Input className="w-24" type="number" placeholder="Rate" value={it.rate} onChange={(e) => setItems(items.map((x, idx) => idx === i ? { ...x, rate: e.target.value } : x))} />
                {items.length > 1 && <Button size="icon" variant="ghost" onClick={() => setItems(items.filter((_, idx) => idx !== i))}><Trash2 className="w-4 h-4" /></Button>}
              </div>
            ))}
            <Button variant="outline" size="sm" onClick={() => setItems([...items, { description: "", qty: "", rate: "" }])} className="gap-1.5"><Plus className="w-3.5 h-3.5" /> Add item</Button>
          </div>
        )}
        <Button onClick={generate} disabled={busy} className="rounded-full gap-2 w-full" data-testid="v-generate-pdf"><Download className="w-4 h-4" /> Generate PDF</Button>
      </div>
    </div>
  );
}
